import React, { useCallback } from 'react';
import head from 'lodash/head';
import size from 'lodash/size';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';

import {
  ChangeLifestylesFiltersFunc,
  FetchLifestylesFilterFields,
  LifestyleCategoryID
} from '../../../../../../../../lifestyles/lifestylesTypes';
import {
  CategoryID,
  CategoryLocalizedName
} from '../../../../../../../../categories/categoriesTypes';
import { IdFilterType } from '../../../../../../../../../types';
import { IconsEnum } from '../../../../../../../../../assets/icons/types';

import { Translate } from '../../../../../../../../../helpers/Translate';
import { PureIconButtonHelper } from '../../../../../../../../../helpers/buttons/PureIconButtonHelper';

import { categoriesKeys } from '../../../../../../../../../locales/keys';

interface LifestylesCategoriesFilterBadgeProps {
  changeLifestylesFilters: ChangeLifestylesFiltersFunc;
  setCheckedCategories: (
    checkedCategories: {
      id: CategoryID;
      localizedName: CategoryLocalizedName;
    }[]
  ) => void;
  checkedCategories: { id: CategoryID; localizedName: CategoryLocalizedName }[];
  defaultLifestyleCategoryId?: IdFilterType;
  defaultLifestyleParentCategoryIds?: LifestyleCategoryID[];
}

function LifestylesCategoriesFilterBadge({
  changeLifestylesFilters,
  setCheckedCategories,
  checkedCategories,
  defaultLifestyleCategoryId,
  defaultLifestyleParentCategoryIds
}: LifestylesCategoriesFilterBadgeProps) {
  const handleRemoveFilter = useCallback(() => {
    const resetFilters = compact([
      !isEmpty(defaultLifestyleCategoryId?.in)
        ? null
        : FetchLifestylesFilterFields.LIFESTYLE_CATEGORY_ID,
      !isEmpty(defaultLifestyleParentCategoryIds)
        ? null
        : FetchLifestylesFilterFields.LIFESTYLE_PARENT_CATEGORY_IDS
    ]);

    changeLifestylesFilters(
      {
        lifestyleCategoryId: defaultLifestyleCategoryId,
        lifestyleParentCategoryIds: defaultLifestyleParentCategoryIds
      },
      resetFilters
    );

    setCheckedCategories([]);
  }, [
    changeLifestylesFilters,
    defaultLifestyleCategoryId,
    defaultLifestyleParentCategoryIds,
    setCheckedCategories
  ]);

  const valueCount = size(checkedCategories);

  if (valueCount === 0) {
    return null;
  }

  return (
    <div className="relative bg-blue-500 bg-opacity-20 dark:text-gray-200 inline-flex items-center m-0.5 px-3 py-1.5 rounded-md text-gray-800 text-sm">
      <div>
        <Translate id={categoriesKeys.plural} />: &nbsp;
        {valueCount > 1 ? valueCount : head(checkedCategories)?.localizedName}
      </div>

      <PureIconButtonHelper
        className="flex-shrink-0 ml-1 -my-1 -mr-2 h-6 w-6 rounded inline-flex items-center justify-center text-gray-800 dark:text-gray-200 hover:bg-gray-500 hover:bg-opacity-20 hover:text-gray-700 dark:hover:bg-opacity-20 dark:hover:bg-white dark:hover:text-white"
        dataGa="categories-clear"
        icon={IconsEnum.X}
        iconClassName="h-3.5 w-3.5"
        onClick={handleRemoveFilter}
      />
    </div>
  );
}

export default LifestylesCategoriesFilterBadge;
